import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';

import {
  AddressTax,
  TaxesService,
  frequencyLabel,
  taxPortalUrl,
  translateTax,
} from 'src/shared/services/taxes.service';

interface JurisdictionTaxVM {
  id: number;
  name: string;
  calcLine: string;
  formLine: string;
  marketplaceNote: string;
}

@Component({
  templateUrl: 'jurisdiction-details.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class JurisdictionDetailsPage implements OnInit {

  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private taxesService = inject(TaxesService);
  private util = inject(Util);

  loaded = false;
  isRightSideContent = true;
  jurisdictionName = '';
  levelLabel = '';
  agency = '';
  portalUrl = '';
  filingMethod = '';
  registrationSteps: string[] = [];
  taxes: JurisdictionTaxVM[] = [];

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const dialogParams = await this.storage.retrieve('dialog-params');
    const propertyId = this.navCtrl.getParam('propertyId') ?? dialogParams?.propertyId;
    const jurisdictionKey = this.navCtrl.getParam('jurisdictionKey') ?? dialogParams?.jurisdictionKey;

    try {
      const taxes = await this.taxesService.getAddressTaxes([propertyId]);
      const inJurisdiction = taxes
        .filter(tax => tax.status !== 'dismissed')
        .filter(tax => (tax.jurisdiction || tax.agency) === jurisdictionKey);
      this.buildView(jurisdictionKey, inJurisdiction);
    } catch (error) {
      this.util.showError(error?.error?.message || error?.message || 'Failed to load jurisdiction');
    }
    this.loaded = true;
  }

  private buildView(jurisdictionKey: string, taxes: AddressTax[]) {
    const first = taxes[0];
    this.jurisdictionName = jurisdictionKey || translateTax('Jurisdiction');
    this.rightSidePanelService.setDialogPageTitle(this.jurisdictionName);
    this.levelLabel = '';
    this.agency = first?.agency || '';
    this.portalUrl = taxPortalUrl(first);
    const registrationUrl = first?.registration_url || '';
    this.filingMethod = registrationUrl ? translateTax('Register at: %url%', { url: registrationUrl }) : '';
    this.registrationSteps = registrationUrl
      ? [translateTax('Register with %agency% at %url%', { agency: this.agency || this.jurisdictionName, url: registrationUrl })]
      : [];
    this.taxes = taxes.map(tax => this.buildTaxVM(tax));
  }

  private buildTaxVM(tax: AddressTax): JurisdictionTaxVM {
    const rate = tax.rate !== null && tax.rate !== undefined ? `${tax.rate}%` : '';
    return {
      id: tax.id,
      name: tax.name,
      calcLine: [rate, frequencyLabel(tax.frequency), tax.due_rule].filter(Boolean).join(' · '),
      formLine: tax.form_number ? translateTax('Form %form%', { form: tax.form_number }) : '',
      marketplaceNote: tax.host_must_file_even_if_platform_collects
        ? translateTax('A return is still required even when platforms collect the tax (zero return).')
        : '',
    };
  }

  goBack() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.back();
    }
  }
}
